import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Grid
} from '@mui/material';
import { Close, CheckCircle, Cancel } from '@mui/icons-material';

const UpdateClaimStatusModal = ({ open, onClose, claim, onUpdate }) => {
  const [status, setStatus] = useState(claim.status);
  const [approvedAmount, setApprovedAmount] = useState(
    claim.approvedAmount || 0
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    if (status === 'Paid' && approvedAmount <= 0) {
      setError('Approved amount is required for paid claims');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await onUpdate(status, status === 'Paid' ? approvedAmount : undefined);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update claim');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>
        <Box display='flex' justifyContent='space-between' alignItems='center'>
          <Typography variant='h6'>Update Claim Status</Typography>
          <Button onClick={onClose} size='small'>
            <Close />
          </Button>
        </Box>
        <Typography variant='subtitle2' color='textSecondary'>
          Claim ID: {claim.carrierClaimId}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity='error' sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Box p={2} bgcolor='grey.50' borderRadius={1} mb={2}>
              <Typography variant='subtitle2' gutterBottom>
                Current Claim Details
              </Typography>
              <Typography variant='body2'>
                Type: {claim.claimType} | Claimed: $
                {claim.claimedAmount.toLocaleString()}
              </Typography>
              {claim.qtyDamaged && (
                <Typography variant='body2'>
                  Quantity: {claim.qtyDamaged}
                </Typography>
              )}
            </Box>
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={status}
                label='Status'
                onChange={e => setStatus(e.target.value)}
              >
                <MenuItem value='Submitted'>Submitted</MenuItem>
                <MenuItem value='Rejected'>Rejected</MenuItem>
                <MenuItem value='Paid'>Paid</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {status === 'Paid' && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                type='number'
                label='Approved Amount ($)'
                value={approvedAmount}
                onChange={e =>
                  setApprovedAmount(parseFloat(e.target.value) || 0)
                }
                inputProps={{ min: 0, max: claim.claimedAmount, step: 0.01 }}
                helperText={`Maximum: $${claim.claimedAmount.toLocaleString()}`}
              />
            </Grid>
          )}

          {status === 'Rejected' && (
            <Grid item xs={12}>
              <Alert severity='info'>
                Claim will be marked as rejected. No payment will be processed.
              </Alert>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant='contained'
          color={status === 'Rejected' ? 'error' : 'primary'}
          disabled={loading}
          startIcon={status === 'Rejected' ? <Cancel /> : <CheckCircle />}
        >
          {status === 'Rejected' ? 'Reject Claim' : 'Update Status'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UpdateClaimStatusModal;
